import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, ShieldOff, Mail, Phone, Package, X, Check, RefreshCw, ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';

// Support enquiries and returns are one destination in the sidebar, joined by
// this strip. Each keeps its own route, data and actions.
const SECTION_TABS = [
  { to: '/admin/customer-service', label: 'SUPPORT ENQUIRIES' },
  { to: '/admin/returns', label: 'RETURNS & RMAS' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  approved: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  received: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  refunded: 'bg-green-500/10 text-green-400 border-green-500/20',
  cancelled: 'bg-white/5 text-white/40 border-white/10',
};

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  received: 'Received',
  refunded: 'Refunded',
  cancelled: 'Cancelled',
};

const STATUS_FLOW = ['pending', 'approved', 'received', 'refunded', 'rejected', 'cancelled'];

const REASON_LABELS = {
  faulty: 'Faulty',
  damaged: 'Damaged',
  misdescribed: 'Misdescribed',
  wrong_item: 'Wrong item',
  changed_mind: 'Changed mind',
  other: 'Other',
};

export default function AdminReturns() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser, isLoadingAuth } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['admin-rma-requests'],
    enabled: isAdmin,
    queryFn: () => base44.entities.RmaRequest.list('-created_date', 1000),
  });

  const filtered = useMemo(() => {
    return requests.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          r.customer_name?.toLowerCase().includes(q) ||
          r.customer_email?.toLowerCase().includes(q) ||
          r.product_name?.toLowerCase().includes(q) ||
          r.order_number?.toLowerCase().includes(q) ||
          r.id?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [requests, statusFilter, search]);

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await base44.entities.RmaRequest.update(id, { status, ...extra });
      toast({ title: `Marked as ${STATUS_LABELS[status]}` });
      queryClient.invalidateQueries({ queryKey: ['admin-rma-requests'] });
      setSelected((s) => (s && s.id === id ? { ...s, status, ...extra } : s));
    } catch (err) {
      toast({ variant: 'destructive', title: 'Update failed', description: err?.message });
    }
  };

  if (isLoadingAuth || isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center pt-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex flex-col items-center justify-center pt-16 px-6 text-center">
        <ShieldOff className="w-10 h-10 text-white/20 mb-4" />
        <h1 className="text-white font-bold text-xl mb-2">Access Denied</h1>
        <p className="text-white/40 text-sm">You must be an administrator to view this page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16 px-6 lg:px-10 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">ORDERS &amp; CUSTOMERS</p>
          <h1 className="text-white font-black text-3xl uppercase">RMA Requests</h1>
          <p className="text-white/40 text-sm mt-1">{filtered.length} of {requests.length} requests</p>
        </div>

        <AdminSectionTabs tabs={SECTION_TABS} className="mb-6" />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, product, order or RMA ID..."
            className="flex-1 bg-[#111115] border border-white/10 text-white text-sm px-4 py-2.5 focus:outline-none focus:border-primary/50"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#111115] border border-white/10 text-white text-sm px-4 py-2.5 focus:outline-none focus:border-primary/50"
          >
            <option value="all">All Statuses</option>
            {STATUS_FLOW.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="border border-white/5 bg-[#111115] p-12 text-center">
            <p className="text-white/30 text-sm">No return requests found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#111115] border border-white/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <Package className="w-4 h-4 text-primary shrink-0" />
                    <p className="text-white font-bold text-sm truncate">{r.product_name}</p>
                    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border ${STATUS_STYLES[r.status]}`}>
                      {STATUS_LABELS[r.status]}
                    </span>
                  </div>
                  <p className="text-white/50 text-xs">
                    {r.customer_name} · {r.return_type === 'replacement' ? 'Replacement' : 'Refund'} · {REASON_LABELS[r.reason] || r.reason}
                    {r.order_number && ` · Order: ${r.order_number}`}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-white/30">
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.customer_email}</span>
                    {r.customer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.customer_phone}</span>}
                  </div>
                </div>
                <button
                  onClick={() => setSelected(r)}
                  className="text-primary text-xs font-bold tracking-widest uppercase hover:text-primary/80 whitespace-nowrap"
                >
                  Manage →
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Manage Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#111115] border border-white/10 max-w-lg">
          {selected && (
            <RmaDetail
              rma={selected}
              onUpdateStatus={updateStatus}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RmaDetail({ rma, onUpdateStatus }) {
  const [tracking, setTracking] = useState(rma.return_tracking_number || '');
  const [adminNotes, setAdminNotes] = useState(rma.admin_notes || '');

  return (
    <div>
      <DialogHeader>
        <DialogTitle className="text-white font-black uppercase text-lg">RMA · {rma.product_name}</DialogTitle>
      </DialogHeader>
      <p className="text-white/30 text-xs font-mono break-all mb-4">{rma.id}</p>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Detail label="Customer" value={rma.customer_name} />
          <Detail label="Email" value={rma.customer_email} />
          <Detail label="Phone" value={rma.customer_phone || '—'} />
          <Detail label="Order Number" value={rma.order_number || '—'} />
          <Detail label="Reason" value={REASON_LABELS[rma.reason] || rma.reason} />
          <Detail label="Requesting" value={rma.return_type === 'replacement' ? 'Replacement' : 'Refund'} />
        </div>

        {rma.description && (
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-1.5">Customer Details</p>
            <p className="text-white/60 text-sm leading-relaxed bg-[#0d0d0f] border border-white/5 p-3">{rma.description}</p>
          </div>
        )}

        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-1.5">Return Tracking (customer)</p>
          <input
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="Tracking number for return shipment"
            className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-primary/50"
          />
        </div>

        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-1.5">Admin Notes</p>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={3}
            className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-primary/50"
          />
        </div>

        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-2">Update Status</p>
          <div className="flex flex-wrap gap-2">
            <ActionBtn icon={Check} label="Approve" onClick={() => onUpdateStatus(rma.id, 'approved', { return_tracking_number: tracking, admin_notes: adminNotes })} active={rma.status === 'approved'} tone="blue" />
            <ActionBtn icon={Package} label="Received" onClick={() => onUpdateStatus(rma.id, 'received', { admin_notes: adminNotes })} active={rma.status === 'received'} tone="purple" />
            <ActionBtn icon={RefreshCw} label="Refunded" onClick={() => onUpdateStatus(rma.id, 'refunded', { admin_notes: adminNotes })} active={rma.status === 'refunded'} tone="green" />
            <ActionBtn icon={X} label="Reject" onClick={() => onUpdateStatus(rma.id, 'rejected', { admin_notes: adminNotes })} active={rma.status === 'rejected'} tone="red" />
            <ActionBtn icon={X} label="Cancel" onClick={() => onUpdateStatus(rma.id, 'cancelled', { admin_notes: adminNotes })} active={rma.status === 'cancelled'} tone="muted" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-0.5">{label}</p>
      <p className="text-white/70 text-sm break-words">{value}</p>
    </div>
  );
}

const TONE = {
  blue: 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10',
  purple: 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10',
  green: 'border-green-500/30 text-green-400 hover:bg-green-500/10',
  red: 'border-red-500/30 text-red-400 hover:bg-red-500/10',
  muted: 'border-white/15 text-white/50 hover:bg-white/5',
};

function ActionBtn({ icon: Icon, label, onClick, active, tone }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 border text-xs font-bold tracking-wide px-3 py-2 transition-colors ${active ? 'bg-primary border-primary text-white' : TONE[tone]}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label.toUpperCase()}
    </button>
  );
}